import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import { useLang } from "../i18n/LangProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Menu = () => {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("potence");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/menu`);
        if (!cancelled) setItems(res.data);
      } catch (err) {
        console.error("Failed to load menu", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tabs = [
    { id: "starters", label: t.menu.tabStarters },
    { id: "potence", label: t.menu.tabPotence },
    { id: "meats", label: t.menu.tabMeats },
    { id: "fish", label: t.menu.tabFish },
    { id: "desserts", label: t.menu.tabDesserts },
  ];

  const visibleItems = items
    .filter((it) => it.category === tab)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="bg-surface py-24 md:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col items-center text-center mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="divider-line" />
            <span className="overline">{t.menu.overline}</span>
            <span className="divider-line" />
          </div>
          <h2 className="font-serif font-light text-ivory text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            {t.menu.title}
          </h2>
          <p className="mt-5 max-w-xl text-ivory/65 text-base sm:text-lg leading-relaxed">
            {t.menu.subtitle}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-14 border-y border-edge py-5">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              data-testid={`menu-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`px-5 py-2 text-xs sm:text-sm uppercase tracking-[0.2em] transition-all duration-300 ${
                tab === tb.id
                  ? "text-copper border-b border-copper"
                  : "text-ivory/55 hover:text-ivory border-b border-transparent"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-ivory/50 py-10">…</div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={tab + lang}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45 }}
              className="grid md:grid-cols-2 gap-x-16 gap-y-10 max-w-5xl mx-auto"
            >
              {visibleItems.map((item, i) => {
                const name = lang === "fr" ? item.name_fr : item.name_en;
                const desc = lang === "fr" ? item.desc_fr : item.desc_en;
                return (
                  <motion.div
                    key={item.id}
                    data-testid={`menu-item-${tab}-${i}`}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.5 }}
                    className="group"
                  >
                    <div className="flex items-baseline justify-between gap-4 mb-2">
                      <h3 className="font-serif text-2xl text-ivory leading-tight flex items-center gap-2 group-hover:text-copper transition-colors">
                        {name}
                        {item.signature && (
                          <Star size={14} className="text-copper fill-copper" />
                        )}
                      </h3>
                      <div className="flex-1 border-b border-dashed border-edge/80 mb-1.5" />
                      <span className="font-serif text-xl text-copper whitespace-nowrap">
                        {item.price}{" "}
                        <span className="text-sm">{t.menu.currency}</span>
                        {item.suffix && (
                          <span className="text-xs text-ivory/55 ml-1">
                            {item.suffix}
                          </span>
                        )}
                      </span>
                    </div>
                    {desc && (
                      <p className="text-sm text-ivory/55 leading-relaxed">
                        {desc}
                      </p>
                    )}
                  </motion.div>
                );
              })}
              {visibleItems.length === 0 && (
                <p className="md:col-span-2 text-center text-ivory/40 py-10 font-serif italic">
                  {lang === "fr"
                    ? "Aucun article dans cette catégorie."
                    : "No items in this category."}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        <div className="mt-20 text-center">
          <p className="font-serif italic text-ivory/40 text-sm max-w-xl mx-auto">
            {lang === "fr"
              ? "Prix en CHF, TVA incluse. La provenance des viandes est affichée à l'entrée du restaurant."
              : "Prices in CHF, VAT included. Meat origin displayed at the entrance."}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Menu;
